import { memo, useCallback, useState, useMemo } from 'react';
import { cn, List, TabControl } from '@divlab/divanui';
import { IconAlert } from '@divlab/divanui/icons';

import * as PageProductStore from '@Stores/PageProduct';
import useTranslation from '@Queries/useTranslation';
import ImportantInfo from '@Components/ImportantInfo';
import Document from './elements/Document';
import Columns from './elements/Columns';
import ModuleCounter from './elements/ModuleCounter';
import PartsTable from './elements/PartsTable';
import SynchronousSchemes from './elements/SynchronousSchemes';
import styles from './Characteristics.module.css';

import type { FC, HTMLAttributes, MouseEvent } from 'react';
import type { Tab, SelectItemData } from '@divlab/divanui';
import type { ModuleProductData } from '@Types/ModuleProduct';
import type { DocumentData, AdditionalImage, Parameter } from '@Pages/PageProduct';

export type CharacteristicsTab = Tab<AdditionalImage[]>;

export interface SizesVariantsData {
  title: string;
  items: SelectItemData[];
}

export interface CharacteristicsProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  title: string;
  tabs?: CharacteristicsTab[];
  parameters: Parameter[];
  needAttention?: boolean;
}

const Characteristics: FC<CharacteristicsProps> = (props) => {
  const { title, tabs, parameters, needAttention, ...restProps } = props;
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(tabs.length > 0 ? tabs[0].id : null);
  const { importantInfo, documents, schemeImages, parts } = PageProductStore.useData();
  const modules = PageProductStore.useModules();
  const hasTabs = tabs?.length > 0;

  const schemesOrientation = schemeImages ? schemeImages.orientation : 'landscape';
  const visibleModules = useMemo(() => {
    if (modules.length < 1) return [];

    return modules.filter((module) => module.count > 0);
  }, [modules]);

  const sizesAttention = useMemo(() => {
    if (!needAttention) return null;
    const size = parameters.find((parameter) => parameter.groupId === 2);
    if (!size) return null;

    const values = size.values.map((value) => `${value.name} ${value.value}`);

    return { title: t('characteristics.size-attention'), value: values };
  }, [parameters, t, needAttention]);

  const handleChangeTab = useCallback((e: MouseEvent, tab: CharacteristicsTab) => {
    setCurrentTab(tab.id);
  }, []);

  const formatedTabs = tabs.map((tab) => ({
    ...tab,
    key: tab.id,
    title: tab.label,
    onClick: (e: MouseEvent) => handleChangeTab(e, tab),
  }));

  return (
    <div {...restProps} className={styles.characteristics}>
      <div className={styles.top}>
        <div className={styles.title}>{title}</div>
        {tabs?.length > 1 && (
          <div>
            <TabControl className={styles.tabs} size='m' itemList={formatedTabs} />
          </div>
        )}
      </div>

      {hasTabs && (
        <div className={styles.schemesWrapper}>
          <List
            className={styles.schemes}
            items={tabs}
            renderChild={(tab: CharacteristicsTab) => {
              if (tab.data?.length < 1) return null;

              return (
                <SynchronousSchemes
                  className={cn(styles.scheme, { [styles.active]: tab.id === currentTab })}
                  images={tab.data}
                  view={schemesOrientation}
                />
              );
            }}
          />

          {sizesAttention && (
            <div className={styles.attention}>
              <IconAlert className={styles.attentionIcon} />
              <div>
                <div className={styles.attentionTitle}>{sizesAttention.title}</div>
                <div className={styles.attentionValue}>
                  {sizesAttention.value.map((val) => (
                    <div key={val}>{val}</div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {parameters?.length > 0 && (
        <Columns parameters={parameters} className={cn(styles.row, styles.columns)} />
      )}

      {visibleModules.length > 0 && (
        <div className={styles.row}>
          <div>
            <div className={styles.title}>{t('parts-title')}</div>
            <List
              className={styles.modules}
              items={visibleModules}
              renderChild={(module: ModuleProductData) => (
                <ModuleCounter className={styles.module} module={module} />
              )}
            />
          </div>
        </div>
      )}
      {parts?.length > 0 && (
        <div className={styles.row}>
          <div className={styles.subTitle}>{t('parts-title')}</div>
          <PartsTable parts={parts} />
        </div>
      )}
      {(importantInfo || documents) && (
        <div className={styles.row}>
          {importantInfo && (
            <ImportantInfo
              className={styles.importantInfo}
              title={importantInfo.title}
              text={importantInfo.text}
            />
          )}
          {documents && (
            <div className={styles.documentsWrapper}>
              <span className={styles.documentsTitle} data-testid='documentation'>
                {documents.title}
              </span>
              <List
                className={styles.documents}
                items={documents.items}
                renderChild={(document: DocumentData) => (
                  <Document
                    className={styles.document}
                    icon={document.icon}
                    name={document.name}
                    sizeInfo={document.sizeInfo}
                    url={document.url}
                  />
                )}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(Characteristics);
